/**
 * Application-wide rule: a link that leaves Insuronix opens in a new tab.
 *
 * Two layers, because links here come from two places:
 *
 * 1. `externalLinkProps()` for links we author. Explicit, visible in the JSX,
 *    and type-checked.
 * 2. `useExternalLinkGuard()` — a MutationObserver mounted once at the app root
 *    that marks anything the first layer missed. Most hrefs on this app are
 *    seeded data (regulator PDFs, bulletin pages, source documents), so new
 *    external links appear without anyone editing a component. The guard means
 *    that still behaves correctly.
 *
 * Patching the attributes rather than intercepting clicks keeps middle-click,
 * ctrl/cmd-click, "Open in new tab" and screen-reader announcements consistent
 * with what the markup says.
 *
 * `rel="noopener noreferrer"`: noopener stops the opened page reaching back
 * through `window.opener` (reverse tabnabbing); noreferrer withholds the
 * referrer, so a regulator's site is not told which page of the app the user
 * came from.
 */

import { useEffect } from 'react'

const REL = 'noopener noreferrer'

/** Schemes that already leave the browser - a new tab would just orphan one. */
const NON_HTTP = /^(mailto:|tel:|sms:|callto:|blob:|data:|javascript:)/i

export function isExternalHref(href: string | null | undefined): boolean {
  if (!href) return false
  const raw = href.trim()
  if (!raw || raw.startsWith('#') || NON_HTTP.test(raw)) return false
  try {
    // Relative hrefs resolve to our own origin and are correctly excluded.
    return new URL(raw, window.location.href).origin !== window.location.origin
  } catch {
    return false
  }
}

/** Spread onto an anchor whose href leaves the app. */
export function externalLinkProps(href: string | null | undefined) {
  return isExternalHref(href)
    ? { target: '_blank' as const, rel: REL }
    : {}
}

function mark(anchor: HTMLAnchorElement): void {
  // Respect an explicit choice already on the element, and never hijack a
  // download - that would open an empty tab beside the saved file.
  if (anchor.hasAttribute('download') || anchor.dataset.sameTab === 'true') return
  if (!isExternalHref(anchor.getAttribute('href'))) return

  if (anchor.getAttribute('target') !== '_blank') anchor.setAttribute('target', '_blank')

  const rel = (anchor.getAttribute('rel') ?? '').split(/\s+/).filter(Boolean)
  for (const token of ['noopener', 'noreferrer']) {
    if (!rel.includes(token)) rel.push(token)
  }
  anchor.setAttribute('rel', rel.join(' '))
}

function markWithin(node: Node): void {
  if (!(node instanceof Element)) return
  if (node instanceof HTMLAnchorElement) mark(node)
  node.querySelectorAll?.('a[href]').forEach((a) => mark(a as HTMLAnchorElement))
}

/**
 * Mount once, at the app root. Marks external anchors already on the page and
 * any that appear later.
 */
export function useExternalLinkGuard(): void {
  useEffect(() => {
    markWithin(document.body)

    const observer = new MutationObserver((records) => {
      for (const record of records) {
        if (record.type === 'childList') {
          record.addedNodes.forEach(markWithin)
        } else if (record.type === 'attributes' && record.target instanceof HTMLAnchorElement) {
          // An href swapped in place (a router re-render, an edited field).
          mark(record.target)
        }
      }
    })

    observer.observe(document.body, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['href'],
    })
    return () => observer.disconnect()
  }, [])
}
